import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FaFire,
} from "react-icons/fa";

import IEHeader from "../components/IEHeader";
import IECategorySlider from "../components/IECategorySlider";
import IENewsCard from "../components/IENewsCard";

import "../styles/infoexchange.css";


// ============================================================
// INFO EXCHANGE
// ============================================================
//
// React-only news system
//
// No Firebase
// No Firestore
// No Google Sheets
// No Apps Script
//
// Google News RSS
//      ↓
// RSS2JSON
//      ↓
// React
//
// ============================================================


// ============================================================
// RSS2JSON
// ============================================================

const RSS2JSON_URL =
  "https://api.rss2json.com/v1/api.json";


// ============================================================
// NUMBER OF ARTICLES PER CATEGORY
// ============================================================

const ARTICLES_PER_CATEGORY = 15;


// ============================================================
// CATEGORY CONFIGURATION
// ============================================================

const NEWS_CATEGORIES = [

  {
    name: "AI",

    query:
      "Artificial Intelligence",

    image:
      "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800",
  },


  {
    name: "Technology",

    query:
      "Technology",

    image:
      "https://images.unsplash.com/photo-1518770660439-4636190af475?w=800",
  },


  {
    name: "Business",

    query:
      "Business",

    image:
      "https://images.unsplash.com/photo-1556740749-887f6717d7e4?w=800",
  },


  {
    name: "Finance",

    query:
      "Finance",

    image:
      "https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?w=800",
  },


  {
    name: "Textile",

    query:
      "Textile industry",

    image:
      "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=800",
  },


  {
    name: "Agriculture",

    query:
      "Agriculture",

    image:
      "https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=800",
  },


  {
    name: "Energy",

    query:
      "Energy",

    image:
      "https://images.unsplash.com/photo-1466611653911-95081537e5b7?w=800",
  },


  {
    name: "Health",

    query:
      "Health",

    image:
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800",
  },


  {
    name: "Education",

    query:
      "Education",

    image:
      "https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800",
  },


  {
    name: "Local",

    query:
      "India local news",

    image:
      "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=800",
  },

];


// ============================================================
// ALL CATEGORY IMAGE
// ============================================================

const ALL_CATEGORY_IMAGE =
  "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800";


// ============================================================
// BUILD GOOGLE NEWS RSS URL
// ============================================================

function buildRSSUrl(query) {

  return (
    "https://news.google.com/rss/search" +
    "?q=" +
    encodeURIComponent(query) +
    "&hl=en-IN" +
    "&gl=IN" +
    "&ceid=IN:en"
  );

}


// ============================================================
// REMOVE HTML
// ============================================================

function stripHTML(value) {

  if (!value) {
    return "";
  }


  const temp =
    document.createElement(
      "div"
    );


  temp.innerHTML =
    value;


  return (
    temp.textContent ||
    temp.innerText ||
    ""
  )
    .replace(/\s+/g, " ")
    .trim();

}


// ============================================================
// CLEAN TITLE
// ============================================================

function cleanTitle(title) {

  if (!title) {
    return "";
  }


  return title
    .replace(
      /\s-\s[^-]+$/,
      ""
    )
    .replace(
      /<[^>]*>/g,
      ""
    )
    .trim();

}


// ============================================================
// EXTRACT SOURCE
// ============================================================

function extractSource(
  title,
  author,
  source
) {

  if (source) {
    return source.trim();
  }


  if (author) {
    return author.trim();
  }


  if (!title) {
    return "Google News";
  }


  const parts =
    title.split(
      " - "
    );


  if (
    parts.length > 1
  ) {

    return parts[
      parts.length - 1
    ].trim();

  }


  return "Google News";

}


// ============================================================
// FETCH ONE CATEGORY
// ============================================================

async function fetchCategory(
  category
) {

  const rssUrl =
    buildRSSUrl(
      category.query
    );


  const requestUrl =
    RSS2JSON_URL +
    "?rss_url=" +
    encodeURIComponent(
      rssUrl
    );


  try {

    const response =
      await fetch(
        requestUrl
      );


    if (!response.ok) {

      throw new Error(
        `HTTP ${response.status}`
      );

    }


    const data =
      await response.json();


    if (
      data.status &&
      data.status !== "ok"
    ) {

      throw new Error(
        data.message ||
        "RSS feed failed"
      );

    }


    const items =
      Array.isArray(
        data.items
      )
        ? data.items
        : [];


    return items
      .slice(
        0,
        ARTICLES_PER_CATEGORY
      )
      .map(
        (
          item,
          index
        ) => {

          const originalTitle =
            item.title ||
            "";


          const title =
            cleanTitle(
              originalTitle
            );


          const source =
            extractSource(
              originalTitle,
              item.author,
              item.source
            );


          const summary =
            stripHTML(
              item.description ||
              item.content ||
              title
            );


          let imageUrl =
            item.thumbnail ||
            "";


          // Some RSS2JSON responses
          // contain enclosure data.

          if (
            !imageUrl &&
            item.enclosure
          ) {

            imageUrl =
              item.enclosure.link ||
              "";

          }


          const createdAt =
            item.pubDate
              ? new Date(
                  item.pubDate
                )
              : new Date();


          return {

            id:
              `${category.name}-${index}-${encodeURIComponent(
                item.guid ||
                item.link ||
                title
              )}`,

            title:

              title ||
              "Latest News",

            summary:

              summary ||
              title,

            imageUrl:

              imageUrl,

            newsUrl:

              item.link ||
              "",

            category:

              category.name,

            source:

              source,

            area:

              category.name === "Local"
                ? "India"
                : "Global",

            featured:

              false,

            likes:

              0,

            views:

              0,

            status:

              "published",

            createdAt:

              createdAt,

          };

        }
      );


  } catch (error) {

    console.error(
      `Info Exchange - ${category.name}:`,
      error
    );


    return [];

  }

}


// ============================================================
// REMOVE DUPLICATES
// ============================================================

function removeDuplicates(
  articles
) {

  const seen =
    new Set();


  return articles.filter(
    (
      article
    ) => {

      const key =
        (
          article.newsUrl ||
          article.title ||
          ""
        )
          .toLowerCase()
          .trim();


      if (!key) {
        return false;
      }


      if (
        seen.has(key)
      ) {

        return false;

      }


      seen.add(key);


      return true;

    }
  );

}


// ============================================================
// SORT NEWEST FIRST
// ============================================================

function sortByDate(
  articles
) {

  return [
    ...articles,
  ].sort(
    (
      a,
      b
    ) => {

      const aTime =
        a.createdAt instanceof Date &&
        !isNaN(
          a.createdAt.getTime()
        )
          ? a.createdAt.getTime()
          : 0;


      const bTime =
        b.createdAt instanceof Date &&
        !isNaN(
          b.createdAt.getTime()
        )
          ? b.createdAt.getTime()
          : 0;


      return (
        bTime -
        aTime
      );

    }
  );

}


// ============================================================
// MAIN COMPONENT
// ============================================================

export default function InfoExchange() {

  // ----------------------------------------------------------
  // NEWS
  // ----------------------------------------------------------

  const [
    news,
    setNews
  ] = useState([]);


  // ----------------------------------------------------------
  // LOADING
  // ----------------------------------------------------------

  const [
    loading,
    setLoading
  ] = useState(true);


  // ----------------------------------------------------------
  // ERROR
  // ----------------------------------------------------------

  const [
    error,
    setError
  ] = useState("");


  // ----------------------------------------------------------
  // SEARCH
  // ----------------------------------------------------------

  const [
    search,
    setSearch
  ] = useState("");


  // ----------------------------------------------------------
  // CATEGORY
  // ----------------------------------------------------------

  const [
    selectedCategory,
    setSelectedCategory
  ] = useState("All");


  // ==========================================================
  // FETCH ALL NEWS
  // ==========================================================

  useEffect(
    () => {

      let cancelled =
        false;


      const loadNews =
        async () => {

          setLoading(
            true
          );


          setError(
            ""
          );


          try {

            // ------------------------------------------------
            // Fetch all categories
            // simultaneously
            // ------------------------------------------------

            const results =
              await Promise.all(
                NEWS_CATEGORIES.map(
                  (
                    category
                  ) =>
                    fetchCategory(
                      category
                    )
                )
              );


            if (
              cancelled
            ) {

              return;

            }


            // ------------------------------------------------
            // Combine
            // ------------------------------------------------

            const combined =
              results.flat();


            // ------------------------------------------------
            // Remove duplicates
            // ------------------------------------------------

            const unique =
              removeDuplicates(
                combined
              );


            // ------------------------------------------------
            // Newest first
            // ------------------------------------------------

            const sorted =
              sortByDate(
                unique
              );


            setNews(
              sorted
            );


            // ------------------------------------------------
            // If nothing loaded
            // ------------------------------------------------

            if (
              sorted.length === 0
            ) {

              setError(
                "Unable to load news right now."
              );

            }

          } catch (err) {

            console.error(
              "Info Exchange error:",
              err
            );


            if (
              !cancelled
            ) {

              setError(
                "Unable to load news right now."
              );

            }

          } finally {

            if (
              !cancelled
            ) {

              setLoading(
                false
              );

            }

          }

        };


      loadNews();


      return () => {

        cancelled =
          true;

      };

    },
    []
  );


  // ==========================================================
  // CATEGORIES
  // ==========================================================

  const categories =
    useMemo(
      () => {

        return [

          {
            name:
              "All",

            image:
              ALL_CATEGORY_IMAGE,

          },

          ...NEWS_CATEGORIES.map(
            (
              category
            ) => ({

              name:
                category.name,

              image:
                category.image,

            })
          ),

        ];

      },
      []
    );


  // ==========================================================
  // FILTER NEWS
  // ==========================================================

  const filteredNews =
    useMemo(
      () => {

        const searchText =
          search
            .trim()
            .toLowerCase();


        return news.filter(
          (
            item
          ) => {

            // ------------------------------------------------
            // CATEGORY
            // ------------------------------------------------

            const categoryMatch =
              selectedCategory ===
              "All"
                ? true
                : item.category ===
                  selectedCategory;


            // ------------------------------------------------
            // SEARCH
            // ------------------------------------------------

            if (
              !searchText
            ) {

              return categoryMatch;

            }


            const searchableText =
              [

                item.title,

                item.summary,

                item.source,

                item.category,

                item.area,

              ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();


            const searchMatch =
              searchableText.includes(
                searchText
              );


            return (
              categoryMatch &&
              searchMatch
            );

          }
        );

      },
      [
        news,
        selectedCategory,
        search,
      ]
    );


  // ==========================================================
  // CATEGORY COUNTS
  // ==========================================================

  const categoryCounts =
    useMemo(
      () => {

        const counts = {};


        news.forEach(
          (
            item
          ) => {

            if (
              !item.category
            ) {

              return;

            }


            counts[
              item.category
            ] =
              (
                counts[
                  item.category
                ] ||
                0
              ) + 1;

          }
        );


        return counts;

      },
      [news]
    );


  // ==========================================================
  // RENDER
  // ==========================================================

  return (

    <div
      className="ie-page"
    >

      {/* ====================================================
          HEADER
      ===================================================== */}

      <IEHeader

        search={
          search
        }

        setSearch={
          setSearch
        }

      />


      {/* ====================================================
          CATEGORIES
      ===================================================== */}

      <IECategorySlider

        categories={
          categories
        }

        selectedCategory={
          selectedCategory
        }

        setSelectedCategory={
          setSelectedCategory
        }

      />


      {/* ====================================================
          SECTION TITLE
      ===================================================== */}

      <div
        className="ie-section-title"
      >

        <FaFire />

        <span>

          {selectedCategory ===
          "All"
            ? "Latest Updates"
            : `${selectedCategory} Updates`}

        </span>

      </div>


      {/* ====================================================
          CATEGORY INFO
      ===================================================== */}

      {!loading &&
        selectedCategory !==
          "All" &&
        categoryCounts[
          selectedCategory
        ] && (

          <div
            className="ie-category-count"
          >

            {categoryCounts[
              selectedCategory
            ]}{" "}

            latest articles

          </div>

        )}


      {/* ====================================================
          LOADING
      ===================================================== */}

      {loading && (

        <div
          className="ie-loading"
        >

          <div
            className="ie-loading-spinner"
          />

          <div>

            Loading latest news...

          </div>

        </div>

      )}


      {/* ====================================================
          ERROR
      ===================================================== */}

      {!loading &&
        error && (

          <div
            className="ie-error"
          >

            {error}

          </div>

        )}


      {/* ====================================================
          EMPTY
      ===================================================== */}

      {!loading &&
        !error &&
        filteredNews.length ===
          0 && (

          <div
            className="ie-empty"
          >

            No news found.

          </div>

        )}


      {/* ====================================================
          NEWS
      ===================================================== */}

      {!loading &&
        filteredNews.length >
          0 && (

          <div
            className="ie-news-list"
          >

            {filteredNews.map(
              (
                item
              ) => (

                <IENewsCard

                  key={
                    item.id
                  }

                  item={
                    item
                  }

                />

              )
            )}

          </div>

        )}

    </div>

  );

}